import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "@/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { CheckCircle, Circle, HelpCircle, Mail, Phone, Shield, XCircle } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import zentroBooksLogo from "@assets/ZentroBooksLogo.png";

// Define the verification code schema
const verificationSchema = z.object({
  code: z
    .string()
    .min(6, "Verification code must be at least 6 characters")
    .max(10, "Verification code must be less than 10 characters"),
});

type VerificationForm = z.infer<typeof verificationSchema>;

export default function VerificationPage() {
  const { user, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<"email" | "phone">("email");
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  const form = useForm<VerificationForm>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      code: "",
    },
  });

  useEffect(() => {
    // If user is already fully verified, redirect to dashboard
    if (user?.phoneVerified || user?.trialVerified) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const sendVerificationCode = async () => {
    try {
      setIsSubmitting(true);
      
      const response = await apiRequest("POST", "/api/verification/send", {
        method: verificationMethod,
      });
      
      if (response.ok) {
        setVerificationSent(true);
        toast({
          title: "Verification code sent",
          description: `We've sent a verification code to your ${verificationMethod}. Please check and enter the code below.`,
        });
      } else {
        const error = await response.json();
        toast({
          title: "Failed to send verification code",
          description: error.message || "Please try again later",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: VerificationForm) => {
    try {
      setIsSubmitting(true);
      
      const response = await apiRequest("POST", "/api/verification/verify", {
        code: data.code,
        method: verificationMethod,
      });
      
      if (response.ok) {
        // Update the user in the cache to get latest verification status
        queryClient.invalidateQueries({ queryKey: ["/api/user"] });
        
        setVerificationSuccess(true);
        toast({
          title: "Account verified",
          description: "Your account has been successfully verified. You can now access all features.",
        });
        
        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        const error = await response.json();
        toast({
          title: "Verification failed",
          description: error.message || "Invalid verification code",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If still loading auth state, show loading spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Verify Your Account | ZentroBooks</title>
        <meta name="description" content="Secure your ZentroBooks account by completing the verification process. Protect your financial data with our multi-step verification." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={zentroBooksLogo} alt="ZentroBooks Logo" className="h-12" />
          </div>
          
          <Card className="w-full bg-white shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-center">Account Verification</CardTitle>
              <CardDescription className="text-center">
                Verify your account to access all features
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {verificationSuccess ? (
                <div className="flex flex-col items-center justify-center py-6 space-y-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                  <h3 className="text-xl font-medium text-center">Verification Complete!</h3>
                  <p className="text-center text-gray-600">
                    Your account has been successfully verified. You'll be redirected to your dashboard momentarily.
                  </p>
                </div>
              ) : (
                <>
                  {/* Verification method selection */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium mb-2">Select verification method:</h3>
                    <div className="flex space-x-3">
                      <Button
                        variant={verificationMethod === "email" ? "default" : "outline"}
                        onClick={() => setVerificationMethod("email")}
                        className="flex-1 flex items-center justify-center gap-2"
                        type="button"
                      >
                        <Mail className="h-4 w-4" />
                        Email
                      </Button>
                      <Button
                        variant={verificationMethod === "phone" ? "default" : "outline"}
                        onClick={() => setVerificationMethod("phone")}
                        className="flex-1 flex items-center justify-center gap-2"
                        type="button"
                      >
                        <Phone className="h-4 w-4" />
                        Phone
                      </Button>
                    </div>
                  </div>
                  
                  {!verificationSent ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg flex items-start gap-3">
                        <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-blue-800 font-medium">Why verify your account?</p>
                          <p className="text-sm text-blue-700">
                            Account verification helps us prevent unauthorized access and protect your financial data. This is a one-time process.
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">
                          We'll send a verification code to:
                        </h4>
                        <div className="flex items-center p-3 border rounded-md bg-gray-50">
                          {verificationMethod === "email" ? (
                            <Mail className="h-5 w-5 text-gray-600 mr-2" />
                          ) : (
                            <Phone className="h-5 w-5 text-gray-600 mr-2" />
                          )}
                          <span className="text-sm">
                            {verificationMethod === "email" 
                              ? user?.email || "your email" 
                              : "your phone number"}
                          </span>
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full"
                        onClick={sendVerificationCode}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Send Verification Code"}
                      </Button>
                    </div>
                  ) : (
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                          control={form.control}
                          name="code"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Verification Code</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter verification code" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <div className="flex flex-col space-y-4">
                          <Button
                            type="submit"
                            className="w-full"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Verifying..." : "Verify Account"}
                          </Button>
                          
                          <Button
                            type="button"
                            variant="link"
                            className="text-sm"
                            onClick={sendVerificationCode}
                            disabled={isSubmitting}
                          >
                            Didn't receive a code? Send again
                          </Button>
                        </div>
                      </form>
                    </Form>
                  )}
                </>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4">
              <div className="w-full pt-4 border-t border-gray-200">
                <div className="flex justify-center space-x-6 text-sm text-gray-500">
                  <Button variant="link" size="sm" className="text-xs text-gray-500">
                    Help
                  </Button>
                  <Button variant="link" size="sm" className="text-xs text-gray-500">
                    Privacy Policy
                  </Button>
                  <Button variant="link" size="sm" className="text-xs text-gray-500">
                    Terms
                  </Button>
                </div>
              </div>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </>
  );
}
